import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Banknote, Briefcase, Eye, Loader2, Pencil, Plus, Printer, Trash2 } from 'lucide-react';
import jalaali from 'jalaali-js';
import { advancesApi, type EmployeeAdvance } from '@/lib/api/advances';
import { getBackendBaseUrl } from '@/lib/api/config';
import { formatRial } from '@/lib/formatMoney';
import {
  buildVoucherNo,
  saveProjectLedgerVoucherPayload,
} from '@/lib/projectLedgerVoucherPrint';
import { amountToPersianWordsRial } from '@/lib/rialWordsFa';
import ConfirmModal from '@/components/shared/ConfirmModal';
import AdvanceModal from './AdvanceModal';
import AttachmentPreviewModal from './AttachmentPreviewModal';
import type { ExpenseEntry } from '@/lib/mock/expenses';

function toJalaliDate(value?: string): string {
  if (!value) return '—';
  if (value.includes('/')) return value;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return value;
  const { jy, jm, jd } = jalaali.toJalaali(parseInt(m[1], 10), parseInt(m[2], 10), parseInt(m[3], 10));
  return `${jy}/${String(jm).padStart(2, '0')}/${String(jd).padStart(2, '0')}`;
}

function attachmentHref(a: EmployeeAdvance): string | null {
  if (a.attachment_url) {
    if (a.attachment_url.startsWith('http')) return a.attachment_url;
    const base = getBackendBaseUrl();
    return base ? `${base}${a.attachment_url}` : a.attachment_url;
  }
  if (!a.attachment_path) return null;
  const base = getBackendBaseUrl();
  return base ? `${base}/storage/${a.attachment_path}` : `/storage/${a.attachment_path}`;
}

type ProjectGroup = {
  projectId: number;
  projectName: string;
  total: number;
  count: number;
  items: EmployeeAdvance[];
};

function groupByProject(advances: EmployeeAdvance[]): ProjectGroup[] {
  const map = new Map<number, ProjectGroup>();
  for (const a of advances) {
    const projectId = a.project_id;
    const projectName = a.project?.name ?? `پروژه ${projectId}`;
    const g = map.get(projectId);
    if (g) {
      g.total += a.amount;
      g.count += 1;
      g.items.push(a);
    } else {
      map.set(projectId, {
        projectId,
        projectName,
        total: a.amount,
        count: 1,
        items: [a],
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.total - a.total);
}

export interface EmployeeAdvancesTabProps {
  employeeId: number;
}

const EmployeeAdvancesTab: React.FC<EmployeeAdvancesTabProps> = ({ employeeId }) => {
  const [advances, setAdvances] = useState<EmployeeAdvance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editAdvance, setEditAdvance] = useState<EmployeeAdvance | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<EmployeeAdvance | null>(null);
  const [preview, setPreview] = useState<{ url: string; name?: string | null } | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    advancesApi
      .list(employeeId)
      .then((r) => setAdvances(r.data?.advances ?? []))
      .catch((e) => setError(e instanceof Error ? e.message : 'خطا در بارگذاری مساعده‌ها'))
      .finally(() => setLoading(false));
  }, [employeeId]);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo(() => groupByProject(advances), [advances]);
  const grandTotal = useMemo(() => advances.reduce((s, a) => s + a.amount, 0), [advances]);

  const openCreate = () => {
    setEditAdvance(null);
    setShowModal(true);
  };

  const openEdit = (a: EmployeeAdvance) => {
    setEditAdvance(a);
    setShowModal(true);
  };

  const openVoucherPrint = (a: EmployeeAdvance) => {
    const typeLabel =
      a.expense_type_title ||
      (a.payment_type === 'on_account'
        ? 'علی‌الحساب'
        : a.payment_type === 'other'
          ? 'سایر پرداخت'
          : 'مساعده');
    const amount = Number(a.amount ?? 0);
    const entryLike: ExpenseEntry = {
      id: `advance-${a.id}`,
      projectId: a.project_id,
      beneficiaryId: `employee-${a.employee_id}`,
      beneficiary: '—',
      expenseTypeId: a.expense_type_id != null ? String(a.expense_type_id) : '',
      amount,
      debit: amount,
      credit: 0,
      dateFa: toJalaliDate(a.registered_at),
      source: a.bsi_statement_registration_id ? 'bsi_bank' : 'advance',
      docNo: a.payroll_month || '',
      accountTitle: typeLabel,
      paymentMethod: 'بانک',
      description: a.notes || typeLabel,
      recordingMode: 'paid',
    };
    saveProjectLedgerVoucherPayload({
      voucherNo: buildVoucherNo(entryLike),
      generatedAt: new Date().toLocaleString('fa-IR'),
      project: {
        id: a.project_id,
        label: a.project?.name ?? `پروژه ${a.project_id}`,
      },
      kind: 'withdrawal',
      kindLabel: 'برداشت',
      dateFa: toJalaliDate(a.registered_at),
      expenseTypeTitle: typeLabel,
      beneficiary: 'کارمند',
      docNo: a.payroll_month || '—',
      credit: 0,
      debit: amount,
      amount,
      amountInWords: amountToPersianWordsRial(amount) || '—',
      paymentMethod: 'بانک',
      description: a.notes || typeLabel,
      sourceLabel: a.bsi_statement_registration_id ? 'گردش بانک' : 'پرداخت / مساعده',
    });
    window.open(`/projects/${a.project_id}/ledger-voucher/print`, '_blank', 'noopener,noreferrer');
  };

  const closeModal = () => {
    setShowModal(false);
    setEditAdvance(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await advancesApi.destroy(employeeId, deleteTarget.id);
      setDeleteTarget(null);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'خطا در حذف');
      setDeleteTarget(null);
    }
  };

  return (
    <div id="employee-advances" className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-5 scroll-mt-24" dir="rtl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-gray-800 font-semibold text-lg">
            <Banknote size={22} className="text-sky-600" />
            مساعده و پرداخت‌ها
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            مساعده، علی‌الحساب و پرداخت‌های بانکی به این شخص — یک نوع حساب واحد.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-sky-600 text-white text-sm font-medium hover:bg-sky-700"
        >
          <Plus size={18} />
          ثبت پرداخت
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-sky-600" size={32} />
        </div>
      ) : advances.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50 p-12 text-center">
          <Banknote size={48} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 mb-4">مساعده‌ای ثبت نشده</p>
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-4 py-2 text-sky-600 hover:bg-sky-50 rounded-xl font-medium"
          >
            <Plus size={18} />
            ثبت اولین مساعده
          </button>
        </div>
      ) : (
        <div className="space-y-5">
          {/* خلاصه کلی */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl border border-sky-100 bg-sky-50/80 px-4 py-3">
              <p className="text-xs text-sky-700/80">جمع کل مساعده‌ها</p>
              <p className="mt-1 text-lg font-bold text-sky-900 tabular-nums">
                {formatRial(grandTotal)}
                <span className="mr-1 text-xs font-normal text-sky-700">ریال</span>
              </p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
              <p className="text-xs text-gray-500">تعداد ثبت‌ها</p>
              <p className="mt-1 text-lg font-bold text-gray-800 tabular-nums">{advances.length}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
              <p className="text-xs text-gray-500">تعداد پروژه‌ها</p>
              <p className="mt-1 text-lg font-bold text-gray-800 tabular-nums">{groups.length}</p>
            </div>
          </div>

          {/* جدول جمع به تفکیک پروژه */}
          <section className="rounded-xl border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200 flex items-center gap-2">
              <Briefcase size={16} className="text-sky-600" />
              <h4 className="text-sm font-semibold text-gray-800">جمع مساعده به تفکیک پروژه</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-right">
                <thead className="bg-white text-gray-500 text-xs">
                  <tr className="border-b border-gray-100">
                    <th className="px-4 py-2.5 font-medium">#</th>
                    <th className="px-4 py-2.5 font-medium">پروژه</th>
                    <th className="px-4 py-2.5 font-medium">تعداد مساعده</th>
                    <th className="px-4 py-2.5 font-medium">جمع دریافتی</th>
                    <th className="px-4 py-2.5 font-medium w-28">سهم از کل</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {groups.map((g, idx) => {
                    const pct = grandTotal > 0 ? Math.round((g.total / grandTotal) * 100) : 0;
                    return (
                      <tr key={g.projectId} className="hover:bg-sky-50/40">
                        <td className="px-4 py-2.5 text-gray-400 tabular-nums">{idx + 1}</td>
                        <td className="px-4 py-2.5 font-medium text-gray-800">{g.projectName}</td>
                        <td className="px-4 py-2.5 tabular-nums text-gray-600">{g.count}</td>
                        <td className="px-4 py-2.5 font-semibold text-gray-900 tabular-nums whitespace-nowrap">
                          {formatRial(g.total)}
                          <span className="mr-1 text-xs font-normal text-gray-500">ریال</span>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-2 min-w-[100px]">
                            <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                              <div
                                className="h-full rounded-full bg-sky-500"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                            <span className="text-xs text-gray-500 tabular-nums w-8">{pct}٪</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-sky-50/60 border-t border-sky-100">
                    <td className="px-4 py-2.5" colSpan={2}>
                      <span className="text-sm font-semibold text-sky-900">جمع کل</span>
                    </td>
                    <td className="px-4 py-2.5 font-semibold text-sky-900 tabular-nums">{advances.length}</td>
                    <td className="px-4 py-2.5 font-bold text-sky-900 tabular-nums whitespace-nowrap">
                      {formatRial(grandTotal)}
                      <span className="mr-1 text-xs font-normal">ریال</span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-sky-700">۱۰۰٪</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </section>

          {/* جزئیات هر پروژه */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-700 px-0.5">جزئیات ثبت‌ها به تفکیک پروژه</h4>
            {groups.map((g) => (
              <section
                key={g.projectId}
                className="rounded-xl border border-gray-200 overflow-hidden"
              >
                <div className="flex flex-wrap items-center justify-between gap-2 bg-gradient-to-l from-sky-50 to-white px-4 py-3 border-b border-gray-200">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-700">
                      <Briefcase size={16} />
                    </span>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{g.projectName}</p>
                      <p className="text-xs text-gray-500">{g.count} ثبت مساعده</p>
                    </div>
                  </div>
                  <div className="text-left shrink-0">
                    <p className="text-[11px] text-gray-500">جمع این پروژه</p>
                    <p className="font-bold text-sky-800 tabular-nums">
                      {formatRial(g.total)}
                      <span className="mr-1 text-xs font-normal text-sky-600">ریال</span>
                    </p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-right">
                    <thead className="bg-gray-50/80 text-gray-500 text-xs">
                      <tr>
                        <th className="px-3 py-2 font-medium">تاریخ ثبت</th>
                        <th className="px-3 py-2 font-medium">ماه کارکرد</th>
                        <th className="px-3 py-2 font-medium">نوع هزینه</th>
                        <th className="px-3 py-2 font-medium">مبلغ</th>
                        <th className="px-3 py-2 font-medium">مبدأ / مقصد</th>
                        <th className="px-3 py-2 font-medium">پیوست</th>
                        <th className="px-3 py-2 font-medium w-32" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {g.items.map((a) => {
                        const href = attachmentHref(a);
                        const src = a.source_bank_account;
                        const dest = a.destination_bank_account;
                        const fromBank = Boolean(a.bsi_statement_registration_id);
                        const typeLabel =
                          a.expense_type_title ||
                          (a.payment_type === 'on_account'
                            ? 'علی‌الحساب'
                            : a.payment_type === 'other'
                              ? 'سایر پرداخت'
                              : 'مساعده');
                        return (
                          <tr key={a.id} className="hover:bg-gray-50/80">
                            <td className="px-3 py-2.5 whitespace-nowrap text-gray-700">
                              {toJalaliDate(a.registered_at)}
                            </td>
                            <td className="px-3 py-2.5 whitespace-nowrap font-mono text-xs text-gray-600">
                              {a.payroll_month || '—'}
                            </td>
                            <td className="px-3 py-2.5 text-xs text-gray-700 whitespace-nowrap">
                              {typeLabel}
                            </td>
                            <td className="px-3 py-2.5 whitespace-nowrap font-semibold text-gray-900 tabular-nums">
                              {formatRial(a.amount)}
                              <span className="mr-1 text-xs font-normal text-gray-500">ریال</span>
                            </td>
                            <td className="px-3 py-2.5 text-xs text-gray-600 max-w-[180px]">
                              {fromBank ? (
                                <span className="text-gray-600 truncate block" title={a.notes || ''}>
                                  {a.notes || 'پرداخت ثبت‌شده'}
                                </span>
                              ) : (
                                <>
                                  <div className="truncate" title={src?.name ?? ''}>
                                    از: {src?.name ?? '—'}
                                  </div>
                                  <div
                                    className="truncate"
                                    title={dest?.bank?.name ?? dest?.account_holder_name ?? ''}
                                  >
                                    به: {dest?.bank?.name ?? dest?.account_holder_name ?? '—'}
                                  </div>
                                </>
                              )}
                            </td>
                            <td className="px-3 py-2.5">
                              {href ? (
                                <button
                                  type="button"
                                  onClick={() => setPreview({ url: href, name: a.attachment_name })}
                                  className="inline-flex items-center gap-1 text-sky-600 hover:underline text-xs"
                                >
                                  <Eye size={14} />
                                  مشاهده
                                </button>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2.5">
                              <div className="flex items-center gap-0.5">
                                <button
                                  type="button"
                                  onClick={() => openVoucherPrint(a)}
                                  className="p-1.5 text-emerald-700 hover:bg-emerald-50 rounded-lg"
                                  title="چاپ فیش"
                                >
                                  <Printer size={15} />
                                </button>
                                {fromBank ? (
                                  <span className="text-[11px] text-gray-400 px-1" title="از بخش اینترنت‌بانک ویرایش شود">
                                    بانک
                                  </span>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => openEdit(a)}
                                      className="p-1.5 text-sky-600 hover:bg-sky-50 rounded-lg"
                                      title="ویرایش"
                                    >
                                      <Pencil size={15} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setDeleteTarget(a)}
                                      className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                                      title="حذف"
                                    >
                                      <Trash2 size={15} />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-gray-50 border-t border-gray-200">
                        <td className="px-3 py-2 text-xs font-medium text-gray-600" colSpan={2}>
                          جمع {g.projectName}
                        </td>
                        <td className="px-3 py-2 font-bold text-gray-900 tabular-nums whitespace-nowrap">
                          {formatRial(g.total)}
                          <span className="mr-1 text-xs font-normal text-gray-500">ریال</span>
                        </td>
                        <td colSpan={3} />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </section>
            ))}
          </div>
        </div>
      )}

      <AdvanceModal
        show={showModal}
        employeeId={employeeId}
        editAdvance={editAdvance}
        onClose={closeModal}
        onSuccess={load}
      />
      <AttachmentPreviewModal
        show={!!preview}
        url={preview?.url ?? null}
        fileName={preview?.name}
        onClose={() => setPreview(null)}
      />
      <ConfirmModal
        show={!!deleteTarget}
        title="حذف مساعده"
        message={
          deleteTarget
            ? `آیا از حذف مساعده ${formatRial(deleteTarget.amount)} ریال${deleteTarget.payroll_month ? ` مربوط به ${deleteTarget.payroll_month}` : ''} مطمئن هستید؟`
            : ''
        }
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default EmployeeAdvancesTab;
